// Controls.jsx
import React, { useEffect, useRef, useState, useContext } from 'react';
import useGameLogic from './UseGameState';
import constants from '../../util/constants';
import { MobileContext } from '../../context/MobileContext';
import '../../App.css';

const CHESSBOARD = constants.CHESSBOARD;

const Controls = ({ undo = false, disableHotkeys = false, moveContext = null }) => {
  const { prevMove, nextMove, goToMove, jumpToLast, currentMove, moves, undoMove, state } = useGameLogic();
  const [isPlaying, setIsPlaying] = useState(false);
  const playingInterval = useRef(null);
  const playSpeed = 1000; // Default speed: 1 move per second
  
  // Get mobile context for responsive styling
  const isMobile = useContext(MobileContext);
  
  // Responsive styles based on mobile context
  const containerStyle = {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '0px',
    gap: isMobile ? '8px' : '10px',
    justifyContent: 'space-between',
    width: isMobile ? '100%' : '535px',
  };
  
  const buttonStyle = {
    border: '3px solid gray',
    height: isMobile ? '50px' : '60px',
    flex: '1',
    color: 'gray',
    fontSize: isMobile ? '18px' : '20px',
    background: 'transparent'
  };

  /**
   * Resolve a path to find the target node in the move tree
   * @param {string} path - The full path (e.g., '4|0:2|1:3')
   * @returns {Object} - Information about the resolved node
   */
  const resolvePath = (path) => {
    if (path === '-1') return { node: null, parent: null, variations: [] };
    
    const parts = path.split('|');
    let currentNode = null;
    let parentPath = null;
    let variations = [];
    
    // First part is always main line index
    const mainIdx = parseInt(parts[0], 10);
    if (mainIdx >= 0 && mainIdx < moves.length) {
      currentNode = moves[mainIdx];
      variations = currentNode?.variations || [];
    }
    
    // Handle deeper paths (variations)
    if (parts.length > 1) {
      parentPath = parts[0];
      let currentMoves = moves;
      
      for (let i = 1; i < parts.length; i++) {
        const [varIdx, moveIdx] = parts[i].split(':').map(Number);
        
        if (i === parts.length - 1) {
          // Last segment - set parent path
          parentPath = parts.slice(0, i).join('|');
        } else {
          // Intermediate segment - continue traversing
          const parentIdx = parseInt(parts[i-1], 10);
          if (!currentMoves[parentIdx]?.variations?.[varIdx]) break;
          
          currentMoves = currentMoves[parentIdx].variations[varIdx];
          currentNode = currentMoves[moveIdx];
          variations = currentNode?.variations || [];
        }
      }
    }
    
    return { node: currentNode, parent: parentPath, variations };
  };

  // Determine if we're at the last move in current variation or main line
  const isAtLastMove = () => {
    if (currentMove === '-1') return false;
    
    if (currentMove.includes('|')) {
      // We're in a variation
      const parts = currentMove.split('|');
      const lastPart = parts[parts.length - 1];
      const [varIdx, moveIdx] = lastPart.split(':').map(Number);
      const parentPath = parts.slice(0, -1).join('|');
      const resolvedParent = resolvePath(parentPath);
      
      // Get the variation array
      const variation = parentPath.includes('|')
        ? resolvedParent.node?.variations?.[varIdx]
        : moves[parseInt(parentPath)]?.variations?.[varIdx];
      
      // Check if we're at the last move of this variation
      return variation && moveIdx >= variation.length - 1;
    } else {
      // We're in main line
      return parseInt(currentMove, 10) >= moves.length - 1;
    }
  };

  // Get ordered variations for a parent move (original → author comments → user comments)
  const getOrderedVariations = (parentIndex) => {
    if (parentIndex < 0 || parentIndex >= moves.length) return [];
    
    const parentMove = moves[parentIndex];
    if (!parentMove?.variations) return [];
    
    const variationsWithInfo = parentMove.variations.map((variation, index) => {
      const hasCommentMoves = variation.some(varMove => 
        varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
      );
      
      let variationType = 'original';
      if (hasCommentMoves) {
        // Note: You may need to pass postAuthorId as a prop to determine author comments
        const isAuthorComment = variation.some(varMove => 
          varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
          varMove?.creator?.id === 'POST_AUTHOR_ID' // Replace with actual post author ID
        );
        variationType = isAuthorComment ? 'authorComment' : 'userComment';
      }
      
      return {
        index,
        variation,
        variationType
      };
    });
    
    // Sort by priority: original → authorComment → userComment
    const sortOrder = { 'original': 0, 'authorComment': 1, 'userComment': 2 };
    variationsWithInfo.sort((a, b) => sortOrder[a.variationType] - sortOrder[b.variationType]);
    
    return variationsWithInfo;
  };

  // Get current variation index within ordered variations
  const getCurrentVariationIndex = (parentIndex) => {
    if (!currentMove.includes('|')) return -1;
    
    const parts = currentMove.split('|');
    const currentParentIndex = parseInt(parts[0], 10);
    
    if (currentParentIndex !== parentIndex) return -1;
    
    const [varIdx] = parts[1].split(':').map(Number);
    const orderedVariations = getOrderedVariations(parentIndex);
    
    // Find the position of this variation in the ordered list
    return orderedVariations.findIndex(v => v.index === varIdx);
  };

  // Navigate to a specific variation by ordered index
  const navigateToVariation = (parentIndex, orderedVariationIndex) => {
    const orderedVariations = getOrderedVariations(parentIndex);
    if (orderedVariationIndex < 0 || orderedVariationIndex >= orderedVariations.length) return false;
    
    const targetVariation = orderedVariations[orderedVariationIndex];
    const newMoveId = `${parentIndex}|${targetVariation.index}:0`;
    goToMove(newMoveId);
    return true;
  };

  // Helper function to get the current move node using path resolution
  const getCurrentMoveNode = () => {
    if (currentMove === '-1') return null;
    
    if (currentMove.includes('|')) {
      // We're in a variation - need to traverse the path
      const parts = currentMove.split('|');
      let currentNode = null;
      let currentArray = moves;
      
      // Navigate through the path
      for (let i = 0; i < parts.length; i++) {
        if (i === 0) {
          // Main line index
          const mainIdx = parseInt(parts[i], 10);
          if (mainIdx >= 0 && mainIdx < currentArray.length) {
            currentNode = currentArray[mainIdx];
          } else {
            return null;
          }
        } else {
          // Variation path
          const [varIdx, moveIdx] = parts[i].split(':').map(Number);
          if (currentNode?.variations?.[varIdx]?.[moveIdx]) {
            currentNode = currentNode.variations[varIdx][moveIdx];
            currentArray = currentNode.variations?.[varIdx] || [];
          } else {
            return null;
          }
        }
      }
      return currentNode;
    } else {
      // Main line move
      const idx = parseInt(currentMove, 10);
      return (idx >= 0 && idx < moves.length) ? moves[idx] : null;
    }
  };

  // Helper function to check if current move has nested variations
  const currentMoveHasNestedVariations = () => {
    const currentNode = getCurrentMoveNode();
    return currentNode?.variations && currentNode.variations.length > 0;
  };

  // Helper function to enter the first nested variation from current position
  const enterNestedVariation = () => {
    if (!currentMoveHasNestedVariations()) return false;
    
    // Construct path to first nested variation
    const newPath = `${currentMove}|0:0`;
    goToMove(newPath);
    return true;
  };

  // Helper function to check if we're in a nested variation (depth > 1)
  const isInNestedVariation = () => {
    if (!currentMove.includes('|')) return false;
    const parts = currentMove.split('|');
    return parts.length > 2; // More than main|variation means nested
  };

  // Helper function to go up one level in the variation hierarchy
  const goUpOneVariationLevel = () => {
    if (!currentMove.includes('|')) {
      // In main line, go to start
      goToMove('-1');
      return true;
    }
    
    const parts = currentMove.split('|');
    
    if (parts.length === 2) {
      // We're in a first-level variation, go back to main line parent
      const parentIndex = parseInt(parts[0], 10);
      goToMove(String(parentIndex));
      return true;
    } else {
      // We're in a nested variation, go back to parent variation
      const parentPath = parts.slice(0, -1).join('|');
      goToMove(parentPath);
      return true;
    }
  };

  // Advance to next move in current variation or main line (respects nested boundaries)
  const advanceMove = () => {
    if (currentMove === '-1') {
      // From start position, go to first move
      if (moves.length > 0) {
        goToMove('0');
        return true;
      }
      return false;
    }
    
    if (currentMove.includes('|')) {
      // We're in a variation (could be nested)
      const parts = currentMove.split('|');
      const lastPart = parts[parts.length - 1];
      
      if (lastPart.includes(':')) {
        const [varIdx, moveIdx] = lastPart.split(':').map(Number);
        
        // Get the current variation array using path traversal
        let currentVariation = null;
        
        // ENHANCED: Handle start position variations
        if (parts[0] === '-1') {
          // This is a start position variation
          currentVariation = state?.startPosition?.variations?.[varIdx];
        } else if (parts.length === 2) {
          // First-level variation
          const parentIdx = parseInt(parts[0], 10);
          currentVariation = moves[parentIdx]?.variations?.[varIdx];
        } else {
          // Nested variation - need to traverse the path
          const currentNode = getCurrentMoveNode();
          if (currentNode) {
            // Go back one level to get the parent, then get its variation
            const parentPath = parts.slice(0, -1).join('|');
            const parentNode = getCurrentMoveNode(); // This gets the node at current path
            // We need to get the parent of current node
            const pathParts = currentMove.split('|');
            let node = moves[parseInt(pathParts[0])];
            
            // Navigate to the parent of the current move
            for (let i = 1; i < pathParts.length - 1; i++) {
              const [vIdx, mIdx] = pathParts[i].split(':').map(Number);
              node = node?.variations?.[vIdx]?.[mIdx];
            }
            
            currentVariation = node?.variations?.[varIdx];
          }
        }
        
        if (currentVariation && moveIdx < currentVariation.length - 1) {
          // Move forward within current variation (stays in same nesting level)
          parts[parts.length - 1] = `${varIdx}:${moveIdx + 1}`;
          goToMove(parts.join('|'));
          return true;
        }
      }
      // At end of this variation level, stop (don't jump to parent level)
      
      return false;
    } else {
      // We're in main line
      const moveIdx = parseInt(currentMove, 10);
      if (moveIdx < moves.length - 1) {
        goToMove(String(moveIdx + 1));
        return true;
      }
      // At end of main line, stop playing
      return false;
    }
  };

  // Toggle play/pause functionality
  const togglePlaying = () => {
    // If already playing, stop
    if (isPlaying) {
      setIsPlaying(false);
      clearTimeout(playingInterval.current);
      return;
    }
    
    // If at the last move, go back to start and begin playing
    if (isAtLastMove()) {
      goToMove('-1');
      setIsPlaying(true);
      return;
    }
    
    // Special handling for start position
    if (currentMove === '-1' || !currentMove) {
      if (moves.length > 0) {
        setIsPlaying(true);
      }
      return;
    }
    
    // Otherwise, start playing from current position
    setIsPlaying(true);
  };

  // Effect to handle automated play
  useEffect(() => {
    if (!isPlaying) return;
    
    playingInterval.current = setTimeout(() => {
      const canContinue = advanceMove();
      
      // If we can't continue (reached the end), stop playing
      if (!canContinue) {
        setIsPlaying(false);
      }
    }, playSpeed);
    
    // Cleanup timeout on unmount or when isPlaying changes
    return () => {
      if (playingInterval.current) {
        clearTimeout(playingInterval.current);
      }
    };
  }, [isPlaying, currentMove]);

  // Keyboard navigation effect
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip all keyboard handling if hotkeys are disabled (e.g., when typing in comment inputs)
      if (disableHotkeys) {
        
        return;
      }
      
      const key = e.key;
      
      // Skip if modifier keys are pressed
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      
      // Space bar to toggle play/pause
      if (key === ' ' || key === 'Spacebar') {
        e.preventDefault(); // Prevent scrolling
        togglePlaying();
        return;
      }
      
      // Pause if any other navigation key is pressed
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key) && isPlaying) {
        setIsPlaying(false);
        clearTimeout(playingInterval.current);
      }
      
      if (key === 'ArrowLeft') {
        // ← LEFT: Move backward in current variation
        // ENHANCED PROTECTION: Multiple checks to prevent bugs when at start position
        if (currentMove === '-1' || currentMove === null || currentMove === undefined) {
          
          e.preventDefault(); // Prevent any default browser behavior
          return; // Exit early to prevent any navigation
        }
        
        // Additional safety check: if currentMove is somehow an invalid string
        if (typeof currentMove === 'string' && currentMove.trim() === '') {
          
          e.preventDefault();
          return;
        }
        
        // Additional safety check: if we're somehow at move index 0 in main line
        if (!currentMove.includes('|') && parseInt(currentMove, 10) === 0) {
          
          goToMove('-1');
          return;
        }
        
        if (currentMove.includes('|')) {
          // We're in a variation
          const parts = currentMove.split('|');
          const lastPart = parts[parts.length - 1];
          
          if (lastPart.includes(':')) {
            const [varIdx, moveIdx] = lastPart.split(':').map(Number);
            
            if (moveIdx > 0) {
              // Move back within variation
              parts[parts.length - 1] = `${varIdx}:${moveIdx - 1}`;
              goToMove(parts.join('|'));
            } else {
              // At start of variation, pop back to parent
              const parentPath = parts.slice(0, -1).join('|');
              
              goToMove(parentPath);
            }
          }
        } else {
          // We're in main line - use prevMove function
          
          prevMove();
        }
      } else if (key === 'ArrowRight') {
        // → RIGHT: Move forward in current variation (respects nesting boundaries)
        if (currentMove.includes('|')) {
          // We're in a variation (could be nested)
          const parts = currentMove.split('|');
          const lastPart = parts[parts.length - 1];
          
          if (lastPart.includes(':')) {
            const [varIdx, moveIdx] = lastPart.split(':').map(Number);
            
            // Get the current variation array (same logic as advanceMove)
            let currentVariation = null;
            
            // ENHANCED: Handle start position variations
            if (parts[0] === '-1') {
              // This is a start position variation
              currentVariation = state?.startPosition?.variations?.[varIdx];
            } else if (parts.length === 2) {
              // First-level variation
              const parentIdx = parseInt(parts[0], 10);
              currentVariation = moves[parentIdx]?.variations?.[varIdx];
            } else {
              // Nested variation - traverse to get the right array
              const pathParts = currentMove.split('|');
              let node = moves[parseInt(pathParts[0])];
              
              // Navigate to the parent of the current move
              for (let i = 1; i < pathParts.length - 1; i++) {
                const [vIdx, mIdx] = pathParts[i].split(':').map(Number);
                node = node?.variations?.[vIdx]?.[mIdx];
              }
              
              currentVariation = node?.variations?.[varIdx];
            }
            
            if (currentVariation && moveIdx + 1 < currentVariation.length) {
              // Move forward within current variation level
              parts[parts.length - 1] = `${varIdx}:${moveIdx + 1}`;
              goToMove(parts.join('|'));
              // console.log(`🎯 [Controls] RIGHT arrow: Navigated to ${parts.join('|')}`);
            } else {
              // console.log(`🔚 [Controls] RIGHT arrow: At end of variation`);
            }
          }
        } else {
          // We're in main line
          nextMove();
        }
      } else if (key === 'ArrowUp') {
        // ↑ UP: Move up one level in the variation hierarchy
        if (currentMove === '-1') return; // Already at root
        
        if (isInNestedVariation()) {
          // We're in a nested variation (depth > 1), go back to parent variation level
          
          goUpOneVariationLevel();
        } else if (currentMove.includes('|')) {
          // We're in a first-level variation
          const parts = currentMove.split('|');
          const parentIndex = parseInt(parts[0], 10);
          const currentVarIndex = getCurrentVariationIndex(parentIndex);
          
          if (currentVarIndex > 0) {
            // Move to previous variation at same level (sibling variation)
            
            navigateToVariation(parentIndex, currentVarIndex - 1);
          } else {
            // We're in the first variation, go back to parent move on main line
            
            goToMove(String(parentIndex));
          }
        } else {
          // Main line, go to start
          
          goToMove('-1');
        }
      } else if (key === 'ArrowDown') {
        // ↓ DOWN: Enter nested variation or move to next sibling variation
        if (currentMove.includes('|')) {
          // We're in a variation - check if current move has nested variations
          if (currentMoveHasNestedVariations()) {
            // Current move has nested variations - enter the first nested variation
            
            enterNestedVariation();
          } else {
            // No nested variations - try to move to next sibling variation
            const parts = currentMove.split('|');
            
            if (isInNestedVariation()) {
              // We're in a nested variation - treat the parent variation as "main"
              // For now, do nothing if no nested variations (could be enhanced later)
              
            } else {
              // We're in a first-level variation
              const parentIndex = parseInt(parts[0], 10);
              
              // ENHANCED: Handle start position variations
              if (parentIndex === -1) {
                // We're in a start position variation - jump to end of current start variation
                const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                const currentVariation = state?.startPosition?.variations?.[varIdx];
                
                if (currentVariation && currentVariation.length > 0) {
                  // Jump to the last move of this start position variation
                  const lastMoveIndex = currentVariation.length - 1;
                  const endPath = `-1|${varIdx}:${lastMoveIndex}`;
                  // console.log(`🎯 [Controls] DOWN arrow: Jumping to end of start variation at ${endPath}`);
                  goToMove(endPath);
                } else {
                  // console.log(`⚠️ [Controls] DOWN arrow: Could not find start position variation ${varIdx}`);
                }
              } else {
                // Regular main line variation - existing logic
                const currentVarIndex = getCurrentVariationIndex(parentIndex);
                const orderedVariations = getOrderedVariations(parentIndex);
                
                // Try to move to next sibling variation
                if (currentVarIndex < orderedVariations.length - 1) {
                  
                  navigateToVariation(parentIndex, currentVarIndex + 1);
                }
                // If we're on the last variation, do nothing (boundary prevention)
              }
            }
          }
        } else {
          // In main line, try to enter variation or jump to end
          if (currentMove === '-1') {
            // At start position, check for start position variations first
            if (state?.startPosition?.variations && state.startPosition.variations.length > 0) {
              // Navigate to first start position variation
              goToMove('-1|0:0');
              return;
            }
            // If no start position variations, jump to last move
            jumpToLastWithContext();
            return;
          }
          
          const idx = parseInt(currentMove, 10);
          const move = moves[idx];
          
          if (move?.variations?.length > 0) {
            // Has variations, enter first one in ordered list
            
            const orderedVariations = getOrderedVariations(idx);
            if (orderedVariations.length > 0) {
              navigateToVariation(idx, 0); // Enter first variation in ordered list
            }
          } else {
            // No variation, jump to end
            
            jumpToLastWithContext();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentMove, moves, goToMove, prevMove, nextMove, jumpToLast, isPlaying, disableHotkeys]);

  // Helper function to check if a move is a comment move
  const isCommentMove = (move) => {
    return move?.creator?.type === 'COMMENT' || 
           move?.creator === 'user' ||
           move?.creator === 'COMMENT' ||
           move?.notation?.includes('(Comment)') ||
           move?.type === 'comment';
  };

  // Enhanced jumpToLast that respects the move context
  const jumpToLastWithContext = () => {
    if (!moves || moves.length === 0) return;
    
    // If moveContext is 'DEFAULT', filter out comment moves from main line
    if (moveContext === 'DEFAULT') {
      // Find the original index of the last non-comment move
      let lastMainLineIndex = -1;
      for (let i = moves.length - 1; i >= 0; i--) {
        if (!isCommentMove(moves[i])) {
          lastMainLineIndex = i;
          break;
        }
      }
      
      if (lastMainLineIndex >= 0) {
        
        goToMove(String(lastMainLineIndex));
      }
    } else {
      // Original behavior - use the jumpToLast from useGameLogic
      jumpToLast();
    }
  };

  return (
    <div style={containerStyle}>
      {undo && (
        <button 
          onClick={() => {
            // Dispatch custom event before calling undoMove
            window.dispatchEvent(new Event('chess-move-undo'));
            undoMove();
          }}
          className="gold-hover-border play-buttons reg-buttons" style={{ 
            ...buttonStyle,
            border: '3px solid #ff0050',
            color: '#ff0050'
          }}
        >
          <i className="fa-solid fa-scissors"></i>
        </button>
      )}
      
      <button 
        onClick={() => goToMove('-1')}
        className="gold-hover-border play-buttons reg-buttons"
        style={buttonStyle}      >
            <i className="fa-solid fa-backward-fast"></i>
            </button>
      <button 
        onClick={prevMove}
        className="gold-hover-border play-buttons reg-buttons"

        style={{
          ...buttonStyle,
          opacity: '1',
          cursor: 'pointer'
        }}      >
            <i className="fa-solid fa-chevron-right fa-rotate-180"></i>
            </button>
      <button 
        onClick={togglePlaying}
        className="red-hover-border play-buttons reg-buttons pause-buttons"
            style={{ 
              ...buttonStyle,
              border: isPlaying ? '3px solid #ff0050' : '3px solid gray',
              color: isPlaying ? '#ff0050' : 'gray'
            }}
      >
{!isPlaying ? (
              <i className="fa-solid fa-play"></i>
            ) : (
              <i className="fa-solid fa-pause"></i>
            )}      </button>
      <button 
        onClick={nextMove}
        style={buttonStyle}
        className="gold-hover-border play-buttons reg-buttons"      >
            <i className="fa-solid fa-chevron-right"></i>
            </button>
      <button 
        onClick={jumpToLastWithContext}
        className="gold-hover-border play-buttons reg-buttons"
        style={buttonStyle}      >
<i className="fa-solid fa-forward-fast"></i>      </button>
      
    </div>
  );
};

export default Controls;